import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api/config';

interface Flow {
  _id: string;
  flowName: string;
  styleConfig: {
    roundedCorners: boolean;
    fontType: string;
    fontSize: string;
    includeImages: boolean;
    baseColors: string[];
  };
  finalScreen: {
    title: string;
    message: string;
    ctaText: string;
    ctaLink: string;
  };
}

interface Question {
  id?: string;
  question: string;
  answers?: string[];
  type: 'single' | 'multiple' | 'number' | 'blood_pressure';
  debugPrompt?: {
    system: string;
    user: string;
    timestamp: string;
  };
  answerImages?: Array<{
    answer: string;
    imageUrl: string | null;
    imagePrompt: string | null;
    error?: string;
  }>;
}

interface SessionData {
  sessionId: string;
  flowId: string;
  totalQuestions: number;
  currentQuestion: number;
  question: Question;
  styleConfig: Flow['styleConfig'];
}

const PublicFlow: React.FC = () => {
  const { flowName } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sessionData, setSessionData] = useState<SessionData | null>(null);
  const [selectedAnswer, setSelectedAnswer] = useState<string | string[]>('');
  const [completed, setCompleted] = useState(false);
  const [finalScreen, setFinalScreen] = useState<Flow['finalScreen'] | null>(
    null
  );
  const [submitting, setSubmitting] = useState(false);
  const [otherText, setOtherText] = useState('');
  const [showOtherInput, setShowOtherInput] = useState(false);
  const bloodPressureValues = typeof selectedAnswer === 'string'
    ? selectedAnswer.split('/')
    : [];
  const hasValidAnswer =
    sessionData?.question.type === 'blood_pressure'
      ? bloodPressureValues.length === 2 && bloodPressureValues.every(
          (value) => /^\d+$/.test(value) &&
            Number.isSafeInteger(Number(value)) && Number(value) > 0
        )
      : sessionData?.question.type === 'number'
      ? typeof selectedAnswer === 'string' &&
        selectedAnswer.trim() !== '' &&
        Number.isFinite(Number(selectedAnswer))
      : Array.isArray(selectedAnswer)
        ? selectedAnswer.length > 0
        : selectedAnswer !== '';

  useEffect(() => {
    startFlow();
  }, [flowName]);

  useEffect(() => {
    // Send height updates to parent window if embedded
    if (window.parent !== window) {
      const sendHeight = () => {
        const height = document.documentElement.scrollHeight;
        window.parent.postMessage(
          {
            type: 'resize',
            flowName: flowName,
            height: height,
          },
          '*'
        );
      };

      // Send initial height
      sendHeight();

      // Send height on resize
      const resizeObserver = new ResizeObserver(sendHeight);
      resizeObserver.observe(document.body);

      return () => resizeObserver.disconnect();
    }
  }, [flowName, sessionData, completed]);

  const startFlow = async () => {
    try {
      const response = await api.post('/responses/start', {
        flowName,
        referrer: new URLSearchParams(window.location.search).get('ref'),
      });
      setSessionData(response.data);
      setSelectedAnswer(response.data.question.type === 'multiple' ? [] : '');
      setLoading(false);
      if (window.parent !== window) {
        window.parent.postMessage(
          {
            type: 'show',
            flowName: flowName,

            questionNumber: response.data.currentQuestion,
            totalQuestions: response.data.totalQuestions,

            questionType: response.data.question.type,
            questionUid:
              response.data.question.id ??
              toSlug(response.data.question.question),
            title: response.data.question.question,
            options: response.data.question.answers,
          },
          '*'
        );
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to start flow');
      setLoading(false);
    }
  };

  const submitAnswer = async () => {
    if (!sessionData || submitting || !hasValidAnswer) {
      return;
    }

    setSubmitting(true);
    try {
      let finalAnswer: string | string[] | number =
        sessionData.question.type === 'blood_pressure'
          ? bloodPressureValues.map(Number).join('/')
          : sessionData.question.type === 'number'
          ? Number(selectedAnswer)
          : selectedAnswer;

      // Handle "Other" text for multiple choice
      if (
        sessionData.question.type === 'multiple' &&
        Array.isArray(selectedAnswer)
      ) {
        finalAnswer = selectedAnswer.map((answer) => {
          if (isOther(answer) && otherText.trim()) {
            return `Other: ${otherText}`;
          }
          return answer;
        });
      }

      const response = await api.post('/responses/answer', {
        sessionId: sessionData.sessionId,
        flowId: sessionData.flowId,
        answer: finalAnswer,
        questionNumber: sessionData.currentQuestion,
        question: sessionData.question,
        type: sessionData.question.type,
        options: sessionData.question.answers,
      });

      if (window.parent !== window) {
        window.parent.postMessage(
          {
            type: 'answer',
            flowName: flowName,

            questionNumber: sessionData.currentQuestion,
            totalQuestions: sessionData.totalQuestions,

            questionType: sessionData.question.type,
            questionUid:
              sessionData.question.id ?? toSlug(sessionData.question.question),
            title: sessionData.question.question,
            answer: finalAnswer,
            options: sessionData.question.answers,
          },
          '*'
        );
      }

      if (response.data.completed) {
        setCompleted(true);
        if (window.parent !== window) {
          window.parent.postMessage(
            {
              type: 'ended',
              flowName: flowName,
            },
            '*'
          );
        }

        if (!noFinalScreen) setFinalScreen(response.data.finalScreen);
      } else {
        setSessionData(response.data);
        setSelectedAnswer(response.data.question.type === 'multiple' ? [] : '');
        setOtherText('');
        setShowOtherInput(false);

        if (window.parent !== window) {
          window.parent.postMessage(
            {
              type: 'show',
              flowName: flowName,

              questionNumber: response.data.currentQuestion,
              totalQuestions: response.data.totalQuestions,

              questionType: response.data.question.type,
              questionUid:
                response.data.question.id ??
                toSlug(response.data.question.question),
              title: response.data.question.question,
              options: response.data.question.answers,
            },
            '*'
          );
        }
      }
    } catch (err) {
      setError('Failed to submit answer');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSingleSelect = async (answer: string) => {
    if (isOther(answer) && !showOtherInput) {
      setSelectedAnswer(answer);
      setShowOtherInput(true);
      return;
    }

    setSelectedAnswer(answer);
    if (!isOther(answer)) {
      setShowOtherInput(false);
    }

    // Automatically submit when single option is selected (but not for "Other" without text)
    if (!submitting && (!isOther(answer) || otherText.trim())) {
      setSubmitting(true);
      try {
        const finalAnswer =
          isOther(answer) && otherText ? `Other: ${otherText}` : answer;
        const response = await api.post('/responses/answer', {
          sessionId: sessionData!.sessionId,
          flowId: sessionData!.flowId,
          answer: finalAnswer,
          questionNumber: sessionData!.currentQuestion,
          question: sessionData!.question,
          type: sessionData!.question.type,
          options: sessionData!.question.answers,
        });

        if (window.parent !== window) {
          window.parent.postMessage(
            {
              type: 'answer',
              flowName: flowName,

              questionNumber: sessionData!.currentQuestion,
              totalQuestions: sessionData!.totalQuestions,

              questionType: sessionData!.question.type,
              questionUid:
                sessionData!.question.id ??
                toSlug(sessionData!.question.question),
              title: sessionData!.question.question,
              answer: finalAnswer,
              options: sessionData!.question.answers,
            },
            '*'
          );
        }

        if (response.data.completed) {
          setCompleted(true);

          if (window.parent !== window) {
            window.parent.postMessage(
              {
                type: 'ended',
                flowName: flowName,
              },
              '*'
            );
          }

          if (!noFinalScreen) setFinalScreen(response.data.finalScreen);
        } else {
          setSessionData(response.data);
          setSelectedAnswer(
            response.data.question.type === 'multiple' ? [] : ''
          );
          setOtherText('');
          setShowOtherInput(false);
        }
      } catch (err) {
        setError('Failed to submit answer');
      } finally {
        setSubmitting(false);
      }
    }
  };

  const handleMultipleSelect = (answer: string) => {
    const current = selectedAnswer as string[];
    if (current.includes(answer)) {
      setSelectedAnswer(current.filter((a) => a !== answer));
      if (isOther(answer)) {
        setShowOtherInput(false);
        setOtherText('');
      }
    } else {
      setSelectedAnswer([...current, answer]);
      if (isOther(answer)) {
        setShowOtherInput(true);
      }
    }
  };

  function toSlug(str: string): string {
    return str
      .replace('?', '')
      .normalize('NFD') // split accented chars
      .replace(/[\u0300-\u036f]/g, '') // remove diacritics
      .replace(/[^a-zA-Z0-9_-]/g, '_') // replace others with underscore
      .toLowerCase();
  }

  function isOther(str: any): boolean {
    return str.toString().toLowerCase().indexOf('other') === 0;
  }

  if (loading || submitting) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div
          className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"
          style={{ borderColor: '#FF6B00' }}
        ></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Oops!</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (completed && finalScreen) {
    const { styleConfig } = sessionData!;
    const fontSize = {
      small: 'text-sm',
      medium: 'text-base',
      large: 'text-lg',
    }[styleConfig.fontSize];

    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{ fontFamily: styleConfig.fontType }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-md w-full text-center"
        >
          <div className="mb-8">
            <div
              className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center"
              style={{ backgroundColor: styleConfig.baseColors[1] }}
            >
              <svg
                className="w-10 h-10 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h1
              className={`font-bold mb-4 ${
                fontSize === 'text-sm'
                  ? 'text-2xl'
                  : fontSize === 'text-base'
                  ? 'text-3xl'
                  : 'text-4xl'
              }`}
              style={{ color: styleConfig.baseColors[0] }}
            >
              {finalScreen.title}
            </h1>
            <p className={`text-gray-600 mb-8 ${fontSize}`}>
              {finalScreen.message}
            </p>
            {finalScreen.ctaLink && finalScreen.ctaText && (
              <a
                href={finalScreen.ctaLink}
                className={`inline-block px-6 py-3 text-white font-medium ${
                  styleConfig.roundedCorners ? 'rounded-lg' : ''
                } ${fontSize}`}
                style={{ backgroundColor: styleConfig.baseColors[0] }}
              >
                {finalScreen.ctaText}
              </a>
            )}
          </div>
        </motion.div>
      </div>
    );
  }

  if (!sessionData) {
    return null;
  }

  const { styleConfig, question, currentQuestion, totalQuestions } =
    sessionData;
  const fontSize = {
    small: 'text-sm',
    medium: 'text-base',
    large: 'text-lg',
  }[styleConfig.fontSize];

  const progress = (currentQuestion / totalQuestions) * 100;
  const showDebug =
    new URLSearchParams(window.location.search).get('debug') === 'true';
  const isEmbedded =
    new URLSearchParams(window.location.search).get('embed') === 'true';
  const noHeader =
    new URLSearchParams(window.location.search).get('noheader') === 'true';
  const noFinalScreen =
    new URLSearchParams(window.location.search).get('nofinalscreen') === 'true';

  return (
    <div
      className={`min-h-screen ${isEmbedded ? '' : 'bg-gray-50'}`}
      style={{ fontFamily: 'Libre Franklin, sans-serif' }}
    >
      {/* Header */}
      {!noHeader && (
        <div className="px-8 pt-8">
          <div className="max-w-4xl mx-auto">
            <img src="/logo-primary.svg" alt="Logo" className="h-8" />
            <div className="mt-2 h-1 bg-gray-200 relative">
              <motion.div
                className="absolute top-0 left-0 h-full"
                style={{ backgroundColor: '#FF6B00' }}
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex justify-center px-4 py-8">
        <div className="w-full max-w-2xl">
          {/* Debug Prompt Display */}
          {showDebug && question.debugPrompt && (
            <div className="mb-4 bg-gray-100 p-4 rounded-lg">
              <h3 className="font-semibold text-sm text-gray-700 mb-2">
                🔍 OpenAI Prompt Debug
              </h3>
              <div className="space-y-2">
                <div>
                  <p className="text-xs font-medium text-gray-600">
                    System Prompt:
                  </p>
                  <p className="text-xs text-gray-700 bg-white p-2 rounded border border-gray-200">
                    {question.debugPrompt.system}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-600">
                    User Prompt:
                  </p>
                  <pre className="text-xs text-gray-700 bg-white p-2 rounded border border-gray-200 whitespace-pre-wrap">
                    {question.debugPrompt.user}
                  </pre>
                </div>
                <p className="text-xs text-gray-500">
                  Timestamp:{' '}
                  {new Date(question.debugPrompt.timestamp).toLocaleString()}
                </p>

                {/* Image Generation Prompts */}
                {styleConfig.includeImages &&
                  question.answerImages &&
                  question.answerImages.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-medium text-gray-600 mb-1">
                        Image Generation Prompts:
                      </p>
                      <div className="space-y-1">
                        {question.answerImages.map((img, idx) => (
                          <div
                            key={idx}
                            className="text-xs bg-white p-2 rounded border border-gray-200"
                          >
                            <span className="font-medium">{img.answer}:</span>
                            {img.imagePrompt ? (
                              <span className="ml-1 text-gray-600">
                                {img.imagePrompt}
                              </span>
                            ) : (
                              <span className="ml-1 text-red-600">
                                {img.error || 'Failed to generate'}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
              </div>
            </div>
          )}

          {/* Question Title */}
          <h1
            className="font-bold text-center mb-8"
            style={{ fontSize: '32px', color: '#505051' }}
          >
            {question.question}
          </h1>

          {/* Options Box */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentQuestion}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-2xl shadow-sm p-8"
            >
              {question.type === 'blood_pressure' ? (
                <div role="group" aria-label={question.question} className="flex items-center gap-3">
                  {['Systolic', 'Diastolic'].map((label, index) => (
                    <React.Fragment key={label}>
                      {index === 1 && <span aria-hidden="true" className="mt-7 text-3xl text-gray-500">/</span>}
                      <label className="flex-1 min-w-0">
                        <span className="block text-sm text-gray-500 mb-2">{label}</span>
                        <input
                          type="number"
                          min="1"
                          step="1"
                          inputMode="numeric"
                          placeholder="___"
                          value={bloodPressureValues[index] ?? ''}
                          onChange={(e) => {
                            const values = [bloodPressureValues[0] ?? '', bloodPressureValues[1] ?? ''];
                            values[index] = e.target.value;
                            setSelectedAnswer(values.join('/'));
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              submitAnswer();
                            }
                          }}
                          disabled={submitting}
                          className={`w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-orange-500 focus:outline-none ${fontSize}`}
                          autoFocus={index === 0}
                        />
                      </label>
                    </React.Fragment>
                  ))}
                </div>
              ) : question.type === 'number' ? (
                <input
                  type="number"
                  step="any"
                  inputMode="decimal"
                  aria-label={question.question}
                  placeholder="Enter a number"
                  value={selectedAnswer as string}
                  onChange={(e) => setSelectedAnswer(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      submitAnswer();
                    }
                  }}
                  disabled={submitting}
                  className={`w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-orange-500 focus:outline-none ${fontSize}`}
                  autoFocus
                />
              ) : (
                <div className="space-y-3 mb-8">
                  {(question.answers ?? []).map((answer, index) => {
                    const imageData = question.answerImages?.find(
                      (img) => img.answer === answer
                    );
                    const hasImage =
                      styleConfig.includeImages && imageData?.imageUrl;

                    const isSelected =
                      question.type === 'single'
                        ? selectedAnswer === answer
                        : (selectedAnswer as string[]).includes(answer);

                    return (
                      <label
                        key={index}
                        className={`flex items-center p-4 border-2 cursor-pointer transition-all rounded-lg ${
                          isSelected
                            ? 'border-orange-500'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                        style={{
                          borderColor: isSelected ? '#FF6D33' : undefined,
                          backgroundColor: 'white',
                        }}
                      >
                        <div className="relative">
                          <input
                            type={
                              question.type === 'single' ? 'radio' : 'checkbox'
                            }
                            name="answer"
                            value={answer}
                            checked={isSelected}
                            onChange={() =>
                              question.type === 'single'
                                ? handleSingleSelect(answer)
                                : handleMultipleSelect(answer)
                            }
                            className="sr-only"
                          />
                          <div
                            className={`w-5 h-5 mr-3 rounded border-2 flex items-center justify-center transition-all ${
                              isSelected
                                ? 'bg-orange-500 border-orange-500'
                                : 'bg-white border-gray-300'
                            }`}
                            style={{
                              backgroundColor: isSelected ? '#FF6D33' : undefined,
                              borderColor: isSelected ? '#FF6D33' : undefined,
                            }}
                          >
                            {isSelected && (
                              <svg
                                className="w-3 h-3 text-white"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            )}
                          </div>
                        </div>
                        {hasImage && (
                          <div className="mr-3">
                            <img
                              src={imageData.imageUrl!}
                              alt={answer}
                              className={`w-16 h-16 object-cover ${
                                styleConfig.roundedCorners ? 'rounded' : ''
                              }`}
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display =
                                  'none';
                              }}
                            />
                          </div>
                        )}
                        <span
                          className={`${fontSize} flex-1`}
                          style={{ color: '#505051' }}
                        >
                          {answer}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}

              {/* Other input field */}
              {showOtherInput &&
                ((question.type === 'single' && isOther(selectedAnswer)) ||
                  (question.type === 'multiple' &&
                    (selectedAnswer as string[]).some((a) => isOther(a)))) && (
                  <div className="mt-4">
                    <input
                      type="text"
                      value={otherText}
                      onChange={(e) => setOtherText(e.target.value)}
                      onKeyPress={(e) => {
                        if (
                          e.key === 'Enter' &&
                          otherText.trim() &&
                          question.type === 'single'
                        ) {
                          handleSingleSelect('other');
                        }
                      }}
                      placeholder="Please specify..."
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-orange-500 focus:outline-none"
                      style={{ borderColor: '#e5e7eb' }}
                      autoFocus
                    />
                    {/* Continue button for single choice "Other" */}
                    {question.type === 'single' && otherText.trim() && (
                      <button
                        onClick={() => handleSingleSelect('other')}
                        className="mt-3 px-6 py-2 text-white font-medium rounded-full flex items-center gap-2 mx-auto transition-all hover:opacity-90"
                        style={{ backgroundColor: '#FF6B00' }}
                      >
                        <span>Continue</span>
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                )}

              {question.type === 'multiple' && (
                <p className={`text-gray-500 mb-4 ${fontSize}`}>
                  Select all that apply
                </p>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Continue Button for answers that require explicit submission */}
          {(question.type === 'multiple' || question.type === 'number' || question.type === 'blood_pressure') && (
            <div className="mt-8 flex justify-center">
              <button
                onClick={submitAnswer}
                disabled={submitting || !hasValidAnswer}
                className={`px-8 py-3 text-white font-medium rounded-full flex items-center gap-2 transition-all ${
                  submitting || !hasValidAnswer
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:opacity-90'
                }`}
                style={{ backgroundColor: '#FF6B00' }}
              >
                <span>Continue</span>
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicFlow;
